/**
 * FeaturedProducts Component - Story 7.1: Create Marketplace Homepage
 * Displays latest 12 products in a responsive grid with loading skeletons, error handling, and React Query
 * Updated for Story 7.6: Uses ErrorMessage component for consistent error display
 */

import React, { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ProductCard } from './ProductCard';
import { ProductCardSkeleton } from './ProductCardSkeleton';
import { ErrorMessage } from './ErrorMessage';
import { fetchFeaturedProducts } from '../services/marketplacePublicService';
import type { FeaturedProductsProps } from '../types/publicMarketplace.types';

/**
 * FeaturedProducts Component
 * Fetches and displays the latest 12 products from the marketplace
 * Features:
 * - Responsive grid (3 columns desktop, 2 tablet, 1 mobile)
 * - Loading skeletons matching final layout
 * - Error state with retry
 * - Empty state
 * - React Query for caching and refetching
 *
 * @component
 * @example
 * <FeaturedProducts
 *   onProductClick={(id) => navigate(`/products/${id}`)}
 *   onLoadComplete={() => console.log('Products loaded')}
 * />
 */
export const FeaturedProducts: React.FC<FeaturedProductsProps> = ({
  onProductClick,
  onLoadComplete,
  className,
}) => {
  // React Query for fetching featured products
  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    isRefetching,
  } = useQuery({
    queryKey: ['products', 'featured'],
    queryFn: () =>
      fetchFeaturedProducts({
        page: 0,
        size: 12,
        sort: 'createdAt,desc',
      }),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  // Call onLoadComplete when data is successfully loaded
  useEffect(() => {
    if (data && !isLoading && !isError && onLoadComplete) {
      onLoadComplete();
    }
  }, [data, isLoading, isError, onLoadComplete]);

  const products = data?.data || [];
  const hasProducts = products.length > 0;

  /**
   * Loading State
   * Show 12 skeleton cards in the same grid layout
   */
  if (isLoading) {
    return (
      <div
        className={cn('w-full', className)}
        role="status"
        aria-live="polite"
        aria-busy="true"
      >
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 12 }).map((_, index) => (
            <ProductCardSkeleton key={`skeleton-${index}`} />
          ))}
        </div>
        <span className="sr-only">Loading featured products...</span>
      </div>
    );
  }

  /**
   * Error State
   * Display error message with retry button
   */
  if (isError) {
    const errorMessage =
      error instanceof Error
        ? error.message
        : 'Failed to load products. Please try again.';
    const statusCode = error && 'statusCode' in error ? (error as any).statusCode : undefined;

    return (
      <div className={cn('w-full', className)} role="alert" aria-live="assertive">
        <div className="mx-auto max-w-2xl">
          <ErrorMessage
            statusCode={statusCode}
            message={errorMessage}
            onRetry={() => refetch()}
            isRetrying={isRefetching}
            variant="default"
          />
        </div>
      </div>
    );
  }

  /**
   * Empty State
   * Display message when no products are available
   */
  if (!hasProducts) {
    return (
      <div className={cn('w-full', className)} role="status">
        <div className="mx-auto flex max-w-md flex-col items-center justify-center py-16 text-center">
          <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-gray-100">
            <span className="text-4xl">📦</span>
          </div>
          <h3 className="mb-2 text-xl font-semibold text-gray-900">
            No Products Available
          </h3>
          <p className="mb-6 text-sm text-gray-600">
            We couldn't find any products at the moment. Please check back later.
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isRefetching}
            className="gap-2"
          >
            <RefreshCw className={cn('h-4 w-4', isRefetching && 'animate-spin')} />
            {isRefetching ? 'Refreshing...' : 'Refresh'}
          </Button>
        </div>
      </div>
    );
  }

  /**
   * Success State
   * Display products in responsive grid
   */
  return (
    <div className={cn('w-full', className)}>
      <div
        className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3"
        role="list"
        aria-label="Featured products"
      >
        {products.map((product) => (
          <ProductCard
            key={product.id}
            product={product}
            onProductClick={onProductClick}
          />
        ))}
      </div>

      {/* Optional: Show total count */}
      {data?.pagination && (
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-600">
            Showing {products.length} of {data.pagination.totalItems} products
          </p>
        </div>
      )}
    </div>
  );
};

export default FeaturedProducts;
